import { useState, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from './useDebounce';
import type { RackEquipment } from '../types';
import type { CatalogEntry, AISuggestion } from '../utils/equipmentApi';

export interface EquipmentFormValues {
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  mounting_position: string;
  weight_kg: number;
  power_w: number;
  serial_number: string;
  asset_tag: string;
  status: string;
  airflow: string;
  depth: string;
  tenant: string;
  attributes: string;
  catalog_id?: number;
}

export interface UseEquipmentFormProps {
  isOpen: boolean;
  initialData?: RackEquipment | null;
  onValidate: (data: any) => Promise<{ errors: string[], warnings: string[] }>;
  onSave: (data: any) => void;
  catalogSearch: (query: string) => Promise<CatalogEntry[]>;
  onCatalogSelect?: (entry: CatalogEntry) => void;
  onAISuggest: (query: string, type: string | undefined, targetU: number, onProgress: (phase: string) => void, signal?: AbortSignal) => Promise<AISuggestion>;
}

export function useEquipmentForm({
  isOpen,
  initialData,
  onValidate,
  onSave,
  catalogSearch,
  onCatalogSelect,
  onAISuggest
}: UseEquipmentFormProps) {
  const [searchResults, setSearchResults] = useState<CatalogEntry[]>([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [validationResult, setValidationResult] = useState<{ errors: string[], warnings: string[] }>({ errors: [], warnings: [] });
  const [aiReasoning, setAiReasoning] = useState<string | null>(null);
  const [isAILoading, setIsAILoading] = useState(false);
  const [aiPhase, setAiPhase] = useState<string>('Запуск ИИ...');

  const form = useForm<EquipmentFormValues>({
    defaultValues: {
      name: '',
      equipment_type: 'server',
      u_height: 1,
      u_position: 1,
      mounting_position: 'Front',
      weight_kg: 10,
      power_w: 0,
      tenant: '',
      attributes: '{}',
      catalog_id: undefined,
    },
  });

  const { watch, reset, setValue } = form;

  const watchedHeight = watch('u_height');
  const watchedPosition = watch('u_position');
  const watchedMounting = watch('mounting_position');
  const watchedWeight = watch('weight_kg');
  const watchedPower = watch('power_w');

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          name: initialData.name,
          equipment_type: initialData.equipment_type,
          u_height: initialData.u_height,
          u_position: initialData.u_position,
          mounting_position: initialData.mounting_position,
          weight_kg: initialData.weight_kg,
          power_w: initialData.power_w || 0,
          tenant: (initialData as any).tenant || (initialData.attributes as any)?.tenant || '',
          attributes: typeof initialData.attributes === 'string'
            ? initialData.attributes
            : JSON.stringify(initialData.attributes ?? {}, null, 2),
        });
      } else {
        reset({
          name: '',
          equipment_type: 'server',
          u_height: 1,
          u_position: 1,
          mounting_position: 'Front',
          weight_kg: 10,
          power_w: 0,
          tenant: '',
          attributes: '{}',
        });
      }
      setSearchResults([]);
      setValidationResult({ errors: [], warnings: [] });
      setAiReasoning(null);
    }
  }, [isOpen, initialData, reset]);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const results = await catalogSearch(debouncedSearchQuery);
        setSearchResults(results);
        setShowCatalog(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    performSearch();
  }, [debouncedSearchQuery, catalogSearch]);

  const handleCatalogSelectInternal = useCallback((entry: CatalogEntry) => {
    setValue('name', `${entry.manufacturer} ${entry.model}`);
    setValue('equipment_type', entry.equipment_type);
    setValue('u_height', entry.u_height);
    setValue('weight_kg', entry.weight_kg || 10);
    setValue('power_w', entry.power_watts || 0);
    setValue('catalog_id', entry.id);
    setShowCatalog(false);
    setSearchResults([]);
    if (onCatalogSelect) {
      onCatalogSelect(entry);
    }
  }, [setValue, onCatalogSelect]);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsAILoading(false);
    }
  }, [isOpen]);

  const handleAISuggest = useCallback(async () => {
    const query = searchQuery || watch('name');
    if (!query || query.length < 2) return;

    setIsAILoading(true);
    setAiReasoning(null);
    setAiPhase('Инициализация ИИ-агента...');

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      const suggestion = await onAISuggest(query, watch('equipment_type'), watch('u_position'), (phase: string) => {
        setAiPhase(phase);
      }, abortController.signal);
      
      if (suggestion.suggested_equipment) {
        const eq = suggestion.suggested_equipment;
        setValue('name', `${eq.manufacturer} ${eq.model}`);
        setValue('equipment_type', eq.equipment_type);
        setValue('u_height', eq.u_height);
        setValue('weight_kg', eq.weight_kg || 10);
        setValue('power_w', eq.power_watts || 0);
      }
      if (suggestion.reasoning) setAiReasoning(suggestion.reasoning);
    } catch (err: any) {
      if (err.message !== 'Aborted' && err.name !== 'CanceledError') {
        setAiReasoning(`Ошибка ИИ: ${err instanceof Error ? err.message : String(err)}`);
      }
    } finally {
      if (abortControllerRef.current === abortController) setIsAILoading(false);
    }
  }, [onAISuggest, watch, setValue, searchQuery]);

  useEffect(() => {
    if (!isOpen) return;
    const validateField = async () => {
      const data = {
        name: watch('name'),
        u_height: watchedHeight,
        u_position: watchedPosition,
        mounting_position: watchedMounting,
        weight_kg: watchedWeight,
        power_w: watchedPower,
      };
      const result = await onValidate(data);
      setValidationResult(result);
    };
    const timeout = setTimeout(validateField, 200);
    return () => clearTimeout(timeout);
  }, [watchedHeight, watchedPosition, watchedMounting, watchedWeight, watchedPower, onValidate, isOpen]);

  const onSubmit = useCallback(async (data: EquipmentFormValues) => {
    const isZeroU = data.mounting_position === 'Left' || data.mounting_position === 'Right' || data.mounting_position === 'Zero-U-Left' || data.mounting_position === 'Zero-U-Right';
    const result = await onValidate({
      ...data,
      is_zero_u: isZeroU,
      u_height: data.u_height,
      u_position: data.u_position,
      weight_kg: data.weight_kg,
      power_w: data.power_w,
    });

    if (result.errors.length > 0) {
      setValidationResult(result);
      return;
    }

    onSave({
      ...data,
      is_zero_u: isZeroU,
      u_height: Number(data.u_height),
      weight_kg: Number(data.weight_kg),
      power_w: Number(data.power_w),
      tenant: data.tenant ? data.tenant.trim() : undefined,
      attributes: (() => {
        try {
          const parsed = JSON.parse(data.attributes || '{}');
          if (data.tenant) parsed.tenant = data.tenant.trim();
          return parsed;
        } catch {
          return data.tenant ? { tenant: data.tenant.trim() } : {};
        }
      })(),
    });
  }, [onSave, onValidate]);

  return {
    form,
    searchQuery,
    setSearchQuery,
    searchResults,
    showCatalog,
    isSearching,
    validationResult,
    aiReasoning,
    isAILoading,
    aiPhase,
    handleCatalogSelect: handleCatalogSelectInternal,
    handleAISuggest,
    onSubmit,
    watchedHeight,
    watchedPosition,
    watchedWeight,
    watchedPower
  };
}
